import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  adminListPublishQueue,
  adminBulkPublish,
  adminGateCheck,
  adminRegenerateDraft,
} from "@/lib/blog-admin.functions";

import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import {
  blogTypeOf,
  blogTypeClass,
  BLOG_TYPE_HELP,
  plannedDayLabel,
  seoScoreClass,
  SEO_SCORE_LABEL,
} from "@/lib/blog/blog-type";

export const Route = createFileRoute("/admin/blog-publish-queue")({
  component: PublishQueue,
});

type Post = {
  id: string;
  slug: string;
  title: string;
  status: "draft" | "scheduled" | "published" | "archived";
  scheduled_at: string | null;
  article_role: "pillar" | "supporting" | null;
  cluster_id: string | null;
  category: string | null;
  hero_image_url: string | null;
  cover_url: string | null;
  seo_score: number | null;
  source: string | null;
  planned_day: string | null;
  planned_date: string | null;
  gsc_query: string | null;
  quality_ready?: boolean | null;
  readiness?: {
    status: "READY TO POST" | "BLOCKED";
    technical_score: number;
    checks: Array<{ id: string; label: string; ok: boolean }>;
    blockers: string[];
    repairs: string[];
    local_intent: boolean;
    local_places: string[];
    scope: string;
    evidence: string;
  } | null;
};

function friendlyRepair(code: string) {
  const [k, v] = code.split(":");
  return (k ?? code).replace(/_/g, " ") + (v ? ` (${v})` : "");
}

function ReadinessPanel({ r }: { r: NonNullable<Post["readiness"]> }) {
  const ready = r.status === "READY TO POST";
  return (
    <div className="mt-2 rounded-lg border border-border bg-muted/30 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 font-bold ${ready ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>{r.status}</span>
        <span className="text-muted-foreground">Technical SEO {r.technical_score}/100</span>
      </div>
      <ul className="mt-2 grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2">
        {r.checks.map((c) => (
          <li key={c.id} className={c.ok ? "text-foreground" : "font-semibold text-destructive"}>
            {c.ok ? "✓" : "✗"} {c.label}
          </li>
        ))}
      </ul>
      {r.blockers.length > 0 && (
        <div className="mt-2">
          <p className="font-semibold text-destructive">What still needs attention:</p>
          <ul className="list-disc pl-4">{r.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}
      <p className="mt-2"><span className="font-semibold">Local:</span> {r.scope}</p>
      <p><span className="font-semibold">Evidence:</span> {r.evidence}</p>
      {r.repairs.length > 0 && (
        <p className="mt-1 text-muted-foreground"><span className="font-semibold">Auto-fixed:</span> {r.repairs.map(friendlyRepair).join("; ")}</p>
      )}
    </div>
  );
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function sourceLabel(s: string | null): { label: string; kind: "pillar" | "supporting" | "gsc" | "manual" | "other" } {
  const v = (s ?? "").toLowerCase();
  if (v.includes("gsc")) return { label: "GSC", kind: "gsc" };
  if (v.includes("pillar")) return { label: "Pillar", kind: "pillar" };
  if (v.includes("supporting") || v.includes("cluster")) return { label: "Supporting", kind: "supporting" };
  if (v === "manual" || v === "") return { label: "Manual", kind: "manual" };
  return { label: s ?? "—", kind: "other" };
}

function sourceClass(kind: string) {
  if (kind === "pillar") return "bg-primary/10 text-primary";
  if (kind === "supporting") return "bg-secondary/60 text-foreground";
  if (kind === "gsc") return "bg-amber-100 text-amber-900";
  if (kind === "manual") return "bg-muted text-muted-foreground";
  return "bg-muted text-foreground";
}

type Cluster = { id: string; name: string; service_page: string | null; status: string };

type GateState = "unknown" | "checking" | "ready" | "blocked";

function weekStart(d: Date) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
}

function inWeek(iso: string | null, offset: number) {
  if (!iso) return offset === 999;
  const s = weekStart(new Date());
  s.setDate(s.getDate() + offset * 7);
  const e = new Date(s);
  e.setDate(e.getDate() + 7);
  const t = new Date(iso).getTime();
  return t >= s.getTime() && t < e.getTime();
}

function PublishQueue() {
  const list = useServerFn(adminListPublishQueue);
  const bulk = useServerFn(adminBulkPublish);
  const check = useServerFn(adminGateCheck);
  const regen = useServerFn(adminRegenerateDraft);
  const qc = useQueryClient();

  const q = useQuery({ queryKey: ["admin-publish-queue"], queryFn: () => list() });
  const posts: Post[] = ((q.data as any)?.posts ?? []) as Post[];
  const clusters: Cluster[] = ((q.data as any)?.clusters ?? []) as Cluster[];

  const [clusterFilter, setClusterFilter] = useState<string>("all");
  const [weekFilter, setWeekFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [gates, setGates] = useState<Record<string, { state: GateState; errors?: string[]; warnings?: string[] }>>({});
  const [publishing, setPublishing] = useState(false);
  const [regening, setRegening] = useState<string | null>(null);
  const [force, setForce] = useState(false);

  const filtered = useMemo(() => {
    const rows = posts.filter((p) => {
      if (clusterFilter !== "all" && (p.cluster_id ?? "") !== clusterFilter) return false;
      if (statusFilter !== "all") {
        if (statusFilter === "ready" && gates[p.id]?.state !== "ready") return false;
        if (statusFilter === "blocked" && gates[p.id]?.state !== "blocked") return false;
        if (statusFilter === "draft" && p.status !== "draft") return false;
        if (statusFilter === "scheduled" && p.status !== "scheduled") return false;
      }
      if (sourceFilter !== "all" && sourceLabel(p.source).kind !== sourceFilter) return false;
      const dateKey = p.planned_date ?? p.scheduled_at;
      if (weekFilter === "this" && !inWeek(dateKey, 0)) return false;
      if (weekFilter === "next" && !inWeek(dateKey, 1)) return false;
      if (weekFilter === "unscheduled" && dateKey) return false;
      return true;
    });
    // Sunday → Saturday by planned/scheduled date; cluster post before GSC post
    // on the same day. Anything with no date at all sinks to the bottom.
    const key = (p: Post) => p.planned_date ?? (p.scheduled_at ? p.scheduled_at.slice(0, 10) : null);
    const rank = (p: Post) => (String(p.source ?? "").includes("gsc") ? 1 : 0);
    return rows.sort((a, b) => {
      const ak = key(a);
      const bk = key(b);
      if (!ak && !bk) return 0;
      if (!ak) return 1;
      if (!bk) return -1;
      if (ak !== bk) return ak < bk ? -1 : 1;
      return rank(a) - rank(b);
    });
  }, [posts, clusterFilter, statusFilter, sourceFilter, weekFilter, gates]);

  const runGate = async (p: Post) => {
    setGates((g) => ({ ...g, [p.id]: { state: "checking" } }));
    try {
      const r = (await check({ data: { id: p.id } })) as {
        ok: boolean;
        errors?: { message: string }[];
        warnings?: { message: string }[];
      };
      setGates((g) => ({
        ...g,
        [p.id]: {
          state: r.ok ? "ready" : "blocked",
          errors: (r.errors ?? []).map((e) => e.message),
          warnings: (r.warnings ?? []).map((w) => w.message),
        },
      }));
    } catch (e) {
      setGates((g) => ({ ...g, [p.id]: { state: "blocked", errors: [(e as Error).message] } }));
    }
  };


  const publishSelected = async () => {
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) return toast.error("Select at least one draft");
    setPublishing(true);
    try {
      const r = (await bulk({ data: { ids, forcePublish: force } })) as {
        results: Array<{ id: string; published: boolean; errors?: string[]; slug?: string }>;
      };
      const okCount = r.results.filter((x) => x.published).length;
      const failCount = r.results.length - okCount;
      toast[okCount ? "success" : "error"](
        `${okCount} published${failCount ? `, ${failCount} blocked` : ""}`,
      );
      setSelected({});
      await qc.invalidateQueries({ queryKey: ["admin-publish-queue"] });
      await qc.invalidateQueries({ queryKey: ["admin-posts"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Bulk publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const regenBlocked = async (id: string) => {
    setRegening(id);
    try {
      await regen({ data: { id } });
      toast.success("Draft regenerated — same role and scheduled day preserved");
      await qc.invalidateQueries({ queryKey: ["admin-publish-queue"] });
      setGates((g) => ({ ...g, [id]: { state: "unknown" } }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Regenerate failed");
    } finally {
      setRegening(null);
    }
  };

  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <div>
      <BlogSubNav />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Publish queue</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Filter drafts and scheduled posts, run the pre-publish gate, and publish in one action.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            Force publish (bypass fact-check warnings)
          </label>
          <Button disabled={publishing || !selCount} onClick={publishSelected}>
            {publishing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Publish selected ({selCount})
          </Button>
        </div>
      </div>

      <div className="mb-4 grid gap-3 rounded-2xl border border-border bg-card p-4 md:grid-cols-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Cluster</label>
          <select
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            value={clusterFilter}
            onChange={(e) => setClusterFilter(e.target.value)}
          >
            <option value="all">All clusters</option>
            <option value="">Unassigned</option>
            {clusters.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Scheduled week</label>
          <select
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            value={weekFilter}
            onChange={(e) => setWeekFilter(e.target.value)}
          >
            <option value="all">Any week</option>
            <option value="this">This week (Sun–Sat)</option>
            <option value="next">Next week</option>
            <option value="unscheduled">Unscheduled</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Source</label>
          <select
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="all">All sources</option>
            <option value="pillar">Pillar (weekly)</option>
            <option value="supporting">Supporting (weekly)</option>
            <option value="gsc">GSC opportunity</option>
            <option value="manual">Manual</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Status</label>
          <select
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="ready">Ready (gate passed)</option>
            <option value="blocked">Blocked (gate failed)</option>
          </select>
        </div>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No posts match this filter.</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="w-10 p-3"></th>
                <th className="p-3 text-left">Title</th>
                <th className="p-3 text-left">Blog type</th>
                <th className="p-3 text-left">Planned day</th>
                <th className="p-3 text-left">{SEO_SCORE_LABEL}</th>
                <th className="p-3 text-left">Cluster</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Gate</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const g = gates[p.id];
                const cluster = clusters.find((c) => c.id === p.cluster_id);
                const src = sourceLabel(p.source);
                const dow = p.scheduled_at ? DOW[new Date(p.scheduled_at).getDay()] : null;
                return (
                  <tr key={p.id} className="border-t border-border align-top">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={!!selected[p.id]}
                        onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.checked }))}
                      />
                    </td>
                    <td className="p-3">
                      <Link to="/admin/posts/$id" params={{ id: p.id }} className="font-semibold hover:underline">
                        {p.title}
                      </Link>
                      <p className="text-xs text-muted-foreground">/blog/{p.slug}</p>
                      {p.readiness ? <ReadinessPanel r={p.readiness} /> : <p className="mt-1 text-[11px] text-muted-foreground">Not checked yet — run Gate.</p>}
                    </td>
                    <td className="p-3">
                      {(() => {
                        const t = blogTypeOf(p);
                        return (
                          <span
                            title={BLOG_TYPE_HELP[t]}
                            className={`rounded-full px-2 py-0.5 text-xs font-bold tracking-wide ${blogTypeClass(t)}`}
                          >
                            {t}
                          </span>
                        );
                      })()}
                      <p className="mt-1 text-[11px] text-muted-foreground">{src.label}</p>
                      {p.gsc_query && (
                        <p className="mt-0.5 text-[11px] text-amber-800">Query: “{p.gsc_query}”</p>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {plannedDayLabel(p.planned_date) ? (
                        <span className="font-semibold text-foreground">{plannedDayLabel(p.planned_date)}</span>
                      ) : p.scheduled_at ? (
                        <span className="text-muted-foreground">
                          {DOW[new Date(p.scheduled_at).getDay()]} · {new Date(p.scheduled_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not planned</span>
                      )}
                      <p className="text-[11px] text-muted-foreground">Planning label only — stays a draft.</p>
                    </td>
                    <td className="p-3">
                      <div className={`text-lg font-bold ${seoScoreClass(p.seo_score)}`}>
                        {p.seo_score == null ? "—" : `${p.seo_score}/100`}
                      </div>
                      <p className="text-[11px] text-muted-foreground">Ranking optimisation score — not a Google score.</p>
                    </td>
                    <td className="p-3 text-xs">{cluster?.name ?? <span className="text-muted-foreground">—</span>}</td>
                    <td className="p-3 text-xs capitalize">{p.status}</td>
                    <td className="p-3 text-xs">
                      {!g || g.state === "unknown" ? (
                        <span className="text-muted-foreground">not run</span>
                      ) : g.state === "checking" ? (
                        <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> checking</span>
                      ) : g.state === "ready" ? (
                        <span className="font-semibold text-green-700">ready</span>
                      ) : (
                        <span className="font-semibold text-destructive" title={(g.errors ?? []).join("\n")}>
                          blocked ({g.errors?.length ?? 0})
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => runGate(p)} disabled={g?.state === "checking"}>
                          <ShieldCheck className="mr-1 h-3 w-3" /> Gate
                        </Button>
                        {g?.state === "blocked" && (
                          <Button size="sm" variant="outline" disabled={regening === p.id} onClick={() => regenBlocked(p.id)}>
                            {regening === p.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
                            Regenerate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
